import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';
import { Card, Button, ConfirmDialog } from '@fiado/react-kit';
import { tenantApi } from '../../api/endpoints/tenant';
import { ContentLoading } from '../shared/ContentLoading';
import { handleApiError } from '../../api/errorMapping';
import { useAuth } from '../../hooks/useAuth';
import { useMyTenantIdentifier } from '../../hooks/useMyTenantIdentifier';
import type { MfaMethodLocal } from '../../api/types/TenantLocal';
import { Gate } from '../permissions/Gate';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
// PROPUESTA AL KIT (DEC-RBAC-045): el kit aún NO exporta un Switch. Mientras tanto se usa el
// Switch local (Radix) que ya existe en el repo, mismo patrón que el Checkbox local de arriba.
// Pendiente de OK de Andres para promoverlo a @fiado/react-kit y migrar este import.
import { Switch } from '../ui/switch';
// DEC-029: catálogo de métodos + regla de guardado (incl. invariante anti-lockout DEC-RBAC-087)
// centralizados en lib/securityPolicy — misma fuente que el drill-down platform y la política de la
// plataforma. Antes este editor NO validaba el método universal (solo length===0) → un tenant podía
// guardar una config sin universal que el backend rechazaba con 400: ahora se bloquea en el front.
import {
    MFA_METHODS,
    securityCannotSave,
    hasUniversalMethod,
    principalOtpLabel,
} from '../../lib/securityPolicy';

export interface SecurityPolicyEditorProps {
    /**
     * Tenant cuya política de seguridad se edita. Si se omite, cae al tenant del usuario logueado
     * (uso de Task 10, pantalla /tenant/security del propio tenant). El módulo Platform lo pasa
     * explícito con el tenantId del PATH para editar OTRO tenant (mismo endpoint /tenants/{tid}/...,
     * el backend re-enforza tenant.security.policy.manage + isPlatform).
     */
    tenantId?: string;
}

export function SecurityPolicyEditor({ tenantId }: SecurityPolicyEditorProps = {}) {
    const { user } = useAuth();
    const tid = tenantId ?? user?.tenantId ?? '';
    // DEC-RBAC-087: el identificador PRINCIPAL del propio tenant (de /me/profile) es lo que decide qué
    // método es "universal". Este editor solo opera sobre el tenant del usuario logueado, así que el
    // identificador del perfil es el correcto.
    const { principalIdentifier } = useMyTenantIdentifier();
    const qc = useQueryClient();
    const queryKey = ['tenant', tid, 'security-policy'];

    const { data, isLoading } = useQuery({
        queryKey,
        queryFn: () => tenantApi.getSecurityPolicy(tid),
        enabled: !!tid,
    });

    // El form se inicializa una sola vez desde el GET; refetches posteriores no lo resetean
    // (mismo patrón que BrandingEditorForm).
    const [selected, setSelected] = useState<MfaMethodLocal[]>([]);
    // DEC-RBAC-045: 2FA prendido/apagado a nivel tenant. Default `true` (secure-by-default) si el
    // GET no trae el campo todavía.
    const [mfaEnabled, setMfaEnabled] = useState(true);
    // Confirmación previa antes de mandar mfaEnabled=false (apagar 2FA debilita la seguridad).
    const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
    const initializedRef = useRef(false);
    useEffect(() => {
        if (!data || initializedRef.current) return;
        setSelected(data.methodsAllowed);
        setMfaEnabled(data.mfaEnabled ?? true);
        initializedRef.current = true;
    }, [data]);

    const toggle = (method: MfaMethodLocal, checked: boolean) => {
        setSelected((prev) =>
            checked ? [...new Set([...prev, method])] : prev.filter((m) => m !== method),
        );
    };

    const mut = useMutation({
        mutationFn: (enabled: boolean) =>
            tenantApi.updateSecurityPolicy(tid, {
                methodsAllowed: selected,
                mfaEnabled: enabled,
            }),
        onSuccess: async () => {
            toast.success('Política de seguridad actualizada');
            await qc.invalidateQueries({ queryKey });
        },
        onError: handleApiError,
    });

    if (isLoading) return <ContentLoading />;

    // DEC-RBAC-087: no se puede guardar sin al menos 1 método NI sin un método universal (TOTP o el OTP
    // del identificador principal). Con 2FA apagado la regla no aplica. Misma lógica que el drill-down
    // platform (securityCannotSave), cerrando la brecha anti-lockout que antes solo validaba el backend.
    const cannotSave = securityCannotSave(
        { mfaEnabled, methodsAllowed: selected },
        principalIdentifier,
    );
    // Aviso específico cuando hay métodos elegidos pero ninguno universal (el único OTP sería el del
    // identificador secundario, que no todos los usuarios tienen).
    const missingUniversal =
        mfaEnabled && selected.length > 0 && !hasUniversalMethod(selected, principalIdentifier);

    // Apagar 2FA pasa por confirmación; prenderlo o guardar con 2FA ya prendido, no.
    const save = () => {
        if (!mfaEnabled) {
            setConfirmDisableOpen(true);
            return;
        }
        mut.mutate(true);
    };

    return (
        <div className="space-y-4">
            <Card
                title="Métodos de verificación en dos pasos (MFA)"
                description="Elige qué segundos factores pueden usar los usuarios de tu tenant al iniciar sesión."
            >
                <div className="flex items-start justify-between gap-4 rounded-md border p-4">
                    <div className="space-y-0.5">
                        <Label htmlFor="mfa-enabled" className="cursor-pointer font-medium">
                            Autenticación en dos pasos (2FA)
                        </Label>
                        <p className="text-xs text-muted-foreground">
                            Si la apagas, los usuarios de tu tenant inician sesión solo con
                            contraseña, sin segundo factor. Recomendado mantenerla activa.
                        </p>
                    </div>
                    <Switch
                        id="mfa-enabled"
                        checked={mfaEnabled}
                        onCheckedChange={(checked) => {
                            setMfaEnabled(checked);
                        }}
                        aria-label="Autenticación en dos pasos (2FA)"
                    />
                </div>

                {/* Con 2FA APAGADO no se muestran los métodos (no aplican). Se prende → aparecen. */}
                {mfaEnabled && (
                    <>
                        <fieldset className="mt-4 space-y-4">
                            <legend className="mb-1 text-sm font-medium">
                                Métodos de verificación permitidos
                            </legend>
                            {MFA_METHODS.map((m) => (
                                <div key={m.value} className="flex items-start gap-3">
                                    <Checkbox
                                        id={`mfa-${m.value}`}
                                        checked={selected.includes(m.value)}
                                        onCheckedChange={(checked) => {
                                            toggle(m.value, checked === true);
                                        }}
                                        className="mt-0.5"
                                    />
                                    <div className="space-y-0.5">
                                        <Label
                                            htmlFor={`mfa-${m.value}`}
                                            className="cursor-pointer"
                                        >
                                            {m.label}
                                        </Label>
                                        <p className="text-xs text-muted-foreground">{m.hint}</p>
                                    </div>
                                </div>
                            ))}
                        </fieldset>

                        <p className="mt-4 flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
                            <ShieldCheck className="mt-0.5 size-4 shrink-0 opacity-70" />
                            <span>
                                El correo electrónico queda siempre disponible como respaldo en
                                casos extremos (recuperación de cuenta), aunque lo desactives aquí.
                            </span>
                        </p>

                        {selected.length === 0 && (
                            <p className="mt-3 text-xs text-destructive">
                                Tienes que permitir al menos un método.
                            </p>
                        )}
                        {missingUniversal && (
                            <p className="mt-3 text-xs text-destructive">
                                El método del identificador secundario no puede ser el único: no
                                todos los usuarios lo tienen. Permite al menos un método universal —{' '}
                                <strong>TOTP</strong> o el código por{' '}
                                <strong>{principalOtpLabel(principalIdentifier)}</strong> (el del
                                identificador principal).
                            </p>
                        )}
                    </>
                )}

                <Gate permission="tenant.security.policy.manage" scope="TENANT" scopeRef={tid}>
                    <div className="mt-4">
                        <Button
                            isLoading={mut.isPending}
                            disabled={cannotSave || mut.isPending}
                            onClick={save}
                        >
                            Guardar
                        </Button>
                    </div>
                </Gate>
            </Card>

            <ConfirmDialog
                open={confirmDisableOpen}
                onOpenChange={setConfirmDisableOpen}
                title="¿Apagar la autenticación en dos pasos?"
                description="Tus usuarios van a poder iniciar sesión solo con contraseña, sin segundo factor. Esto reduce la seguridad de las cuentas del tenant."
                confirmLabel="Apagar 2FA"
                cancelLabel="Cancelar"
                destructive
                loading={mut.isPending}
                onConfirm={() => {
                    mut.mutate(false);
                }}
            />
        </div>
    );
}
